import {
  ARInvoiceAttributes,
  GetInterfaceValidatorConfigResponseObj,
  GetInvoiceDetailsResponseObj,
  IFInvoiceObj,
  IFPayment,
} from "@/types/interfaces/crm";
import ifAlerts from "@/data/ifAlerts.json";
import { risingStarMap } from "@/types/constants/risingStarMap";
import { risingStarData } from "@/types/constants/risingStarData";
import { extractCanadaRegion, regexFromString, resolveProductTier } from "./utils";
import { provinceVatCodeMap } from "@/types/constants/provinceVatCodeMap";
import { fopWithDescription } from "@/types/constants/formOfPayment";
import { ticketNumberRegexes } from "@/types/constants/ticketNumberRegexes";

interface AlertCheck {
  alertId: number;
  description: string;
  successMessage: string;
  errorMessage: string;
}

export const alertChecks: AlertCheck[] = ifAlerts;

export interface ValidateInvoiceProps {
  arInvoices: ARInvoiceAttributes[];
  reservationLines: GetInvoiceDetailsResponseObj["data"]["reservations"];
  ifInvoice: IFInvoiceObj;
  references: GetInvoiceDetailsResponseObj["references"] | undefined;
  interfaceValidatorConfig?: GetInterfaceValidatorConfigResponseObj["data"];
  risingStarLevel: number | null | undefined;
}

export interface ValidationResponse {
  isValid: boolean;
  message?: string;
  description: string;
  affectedLines?: { reservationId?: number; message: string; vendorName?: string; bookingNumber?: string }[];
}

function isSequential(str: string) {
  if (!/^\d+$/.test(str) || str.length < 4) return false;

  let ascending = true;
  let descending = true;
  for (let i = 1; i < str.length; i++) {
    const prev = Number(str[i - 1]);
    const curr = Number(str[i]);
    if ((prev + 1) % 10 !== curr) ascending = false; // 9 -> 0 wrap
    if ((prev + 9) % 10 !== curr) descending = false; // 0 -> 9 wrap
    if (!ascending && !descending) return false;
  }
  return ascending || descending;
}

const toCents = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100);

function getMatchingFop(payment: IFPayment) {
  let filtered = fopWithDescription.filter((fop) => fop.fop === payment.formOfPayment);

  const trimmedAccountNumber = payment.accountNumber?.trim();
  const paymentAccountNumber = trimmedAccountNumber ? parseInt(trimmedAccountNumber) : null;
  const accountFiltered = filtered.filter((fop) => {
    const fopBankAccount = parseInt(fop.bankAccount || "") || null;
    return fopBankAccount === paymentAccountNumber;
  });
  if (accountFiltered.length > 0) {
    filtered = accountFiltered;
  }

  const trimmedPaymentBankNumber = payment.bankNumber?.trim().toLowerCase();
  const paymentBankNumber = trimmedPaymentBankNumber ? trimmedPaymentBankNumber : null;
  const bankFiltered = filtered.filter((fop) => {
    const fopBankNumber = (fop.bankNumber || "").trim().toLowerCase() || null;
    return fopBankNumber === paymentBankNumber;
  });
  if (bankFiltered.length > 0) {
    filtered = bankFiltered;
  }
  return filtered.length > 1 ? filtered : filtered.length === 1 ? filtered[0] : null;
}

export function validateInvoice(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  switch (alertCheck.alertId) {
    case 1:
      return validateAlertId1BranchNo1(alertCheck, props);
    case 2:
      return validateAlertId2PostingMonth(alertCheck, props);
    case 3:
      return validateAlertId3StartsWithBranchNo(alertCheck, props);
    case 4:
      return validateAlertId4TwoAgentsPresent(alertCheck, props);
    case 5:
      return validateAlertId5TicketNumberValidity(alertCheck, props);
    case 6:
      return validateAlertId6CommissionSplit(alertCheck, props);
    case 7:
      return validateAlertId7BookingLineFormOfPayment(alertCheck, props);
    case 8:
      return validateAlertId8AmountDueZero(alertCheck, props);
    case 9:
      return validateAlertId9CurrencyCode(alertCheck, props);
    case 10:
      return validateAlertId10VatCodePresence(alertCheck, props);
    case 12:
      return validateAlertId12FormOfPayment(alertCheck, props);
    case 13:
      return validateAlertId13ReservationCommissionAmount(alertCheck, props);
    case 16:
      return validateAlertId16VendorNameNumeric(alertCheck, props);
    case 17:
      return validateAlertId17InvoiceNumberMatch(alertCheck, props);
    case 18:
      return validateAlertId18LocallyPaidSaleType(alertCheck, props);
    case 19:
      return validateAlertId19ClientAndAgentBranch(alertCheck, props);
    case 20:
      return validateAlertId20LocallyPaidNoARLines(alertCheck, props);
    default:
      return {
        isValid: false,
        message: `Alert ID ${alertCheck.alertId} check is not found`,
        description: `Alert ID ${alertCheck.alertId} check is not found`,
      };
  }
}

function validateAlertId20LocallyPaidNoARLines(
  alertCheck: AlertCheck,
  props: ValidateInvoiceProps,
): ValidationResponse {
  const { reservationLines, references } = props;
  const errorLines: ValidationResponse["affectedLines"] = [];

  let locallyPaidPresent = false;

  for (const reservation of reservationLines || []) {
    const saleTypeObj = references?.saleTypes?.find(
      (saleType) =>
        saleType.profileNumber === reservation.reservation.vendorNumber &&
        saleType.productCode === reservation.reservation.productCode,
    );
    const saleTypeCode = saleTypeObj ? saleTypeObj.saleType : undefined;

    // Sale type 2 = Voucher (locally paid)
    if (saleTypeCode !== 2) {
      continue;
    }

    locallyPaidPresent = true;

    if (reservation.arInvoiceDetails && reservation.arInvoiceDetails.length > 0) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: `Locally paid reservation has ${reservation.arInvoiceDetails.length} AR line(s)`,
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
    }
  }

  if (!locallyPaidPresent) {
    return {
      isValid: true,
      message: "No locally paid reservations present",
      description: alertCheck.description,
    };
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId10VatCodePresence(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { reservationLines, ifInvoice } = props;
  const errorLines: ValidationResponse["affectedLines"] = [];
  const clientAddress = ifInvoice.profileAddress;

  for (const reservation of reservationLines || []) {
    const vatCode = reservation.reservation.vatCode;
    if (!vatCode) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: `VAT code is not present for ${reservation.reservation.vendorName}`,
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
      continue;
    }

    if (reservation.reservation.productCode === 18 && parseInt(reservation.reservation.vendorNumber || "") === 1800) {
      for (const ifInvoiceDetail of reservation.ifInvoiceDetails || []) {
        const vatOnCommissionCents = toCents(ifInvoiceDetail.vatOnCommission ?? 0);
        const hstCents = toCents(ifInvoiceDetail.tax3 ?? 0);
        if (vatOnCommissionCents !== hstCents) {
          errorLines.push({
            reservationId: reservation.reservation.reservationId,
            message: `VAT on commission (${(vatOnCommissionCents / 100).toFixed(2)}) does not equal HST (${(
              hstCents / 100
            ).toFixed(2)}) for service fees`,
            vendorName: reservation.reservation.vendorName,
            bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          });
        }
      }

      if (reservation.reservation.currencyCode === "USD") {
        if (vatCode !== "E") {
          errorLines.push({
            reservationId: reservation.reservation.reservationId,
            message: `Currency is USD and VAT code is not 'E' for service fees`,
            vendorName: reservation.reservation.vendorName,
            bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          });
        }
        continue;
      }
      if (!clientAddress) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: `Client address is not present to validate VAT code for service fees`,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }
      const clientRegionObj = extractCanadaRegion(clientAddress);

      if (clientRegionObj.error) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: clientRegionObj.error,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      } else if (!clientRegionObj.region && vatCode !== "E") {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: `Client address is not in Canada and VAT code is not 'E' for service fees`,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      } else if (
        clientRegionObj.region &&
        vatCode !== provinceVatCodeMap[clientRegionObj.region.toUpperCase() as keyof typeof provinceVatCodeMap]
      ) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: `VAT code '${vatCode}' does not match client region '${clientRegionObj.region}' for service fees`,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId5TicketNumberValidity(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const errorLines: ValidationResponse["affectedLines"] = [];

  for (const reservation of props.reservationLines || []) {
    const ticketNumber = reservation.reservation.ticketNumber || reservation.reservation.recordLocator;
    if (!ticketNumber) {
      errorLines.push({
        message: `Ticket number is not present for ${reservation.reservation.vendorName}`,
        reservationId: reservation.reservation.reservationId,
        vendorName: reservation.reservation.vendorName,
      });
      continue;
    }

    const repeatedNumberRegex = /^(.)\1+$/;
    if (repeatedNumberRegex.test(ticketNumber)) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Ticket number or record locator is invalid (repeated characters)",
        vendorName: reservation.reservation.vendorName,
        bookingNumber: ticketNumber,
      });
    }

    if (isSequential(ticketNumber)) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Ticket number or record locator is invalid (sequential characters)",
        vendorName: reservation.reservation.vendorName,
        bookingNumber: ticketNumber,
      });
    }

    if (ticketNumber.startsWith("000")) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Ticket number or record locator is invalid (starts with 000)",
        vendorName: reservation.reservation.vendorName,
        bookingNumber: ticketNumber,
      });
    }

    try {
      const vendorNumber = Number(reservation.reservation.vendorNumber);
      if (!Number.isFinite(vendorNumber)) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: "Vendor number is not a valid number",
          vendorName: reservation.reservation.vendorName,
          bookingNumber: ticketNumber,
        });
        continue;
      }

      const patterns = ticketNumberRegexes[vendorNumber];
      if (patterns) {
        const regexes = patterns.map((pattern) => regexFromString(pattern));
        const matchesAtLeastOne = regexes.some((regex) => regex.test(ticketNumber));
        if (!matchesAtLeastOne) {
          errorLines.push({
            reservationId: reservation.reservation.reservationId,
            message: "Ticket number or record locator does not match the expected format for the vendor",
            vendorName: reservation.reservation.vendorName,
            bookingNumber: ticketNumber,
          });
        }
      }
    } catch (error) {
      errorLines.push({
        message: `Failed to validate ticket number format for vendor number ${reservation.reservation.vendorNumber} (invalid regex pattern configured)`,
        reservationId: reservation.reservation.reservationId,
        vendorName: reservation.reservation.vendorName,
        bookingNumber: ticketNumber,
      });
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId19ClientAndAgentBranch(
  alertCheck: AlertCheck,
  props: ValidateInvoiceProps,
): ValidationResponse {
  const profilesData = props.references?.profilesData;
  const invoicedBranch = props.ifInvoice.branchNumber;
  const errorLines: ValidationResponse["affectedLines"] = [];

  const clientProfileNumber = props.ifInvoice.profileNumber;
  const clientProfileObj = profilesData?.find((profile) => profile.profileNumber === clientProfileNumber);
  if (clientProfileObj) {
    const clientBranches = clientProfileObj?.branches || [];
    if (!clientBranches.includes(invoicedBranch)) {
      errorLines.push({
        message: `Client branch (${clientBranches.join(", ")}) is not under invoiced branch (${invoicedBranch})`,
      });
    }

    if (!clientProfileObj.active) {
      errorLines.push({
        message: `Client profile (${clientProfileNumber}) is not active`,
      });
    }
  } else {
    errorLines.push({
      message: `Client profile (${clientProfileNumber}) not found in profiles data`,
    });
  }

  const os1AgentProfileNumber = props.ifInvoice.osAgentNumber1;
  const os1AgentProfileObj = profilesData?.find((profile) => profile.profileNumber === os1AgentProfileNumber);
  if (os1AgentProfileObj) {
    const os1AgentBranches = os1AgentProfileObj?.branches || [];
    if (!os1AgentBranches.includes(invoicedBranch)) {
      errorLines.push({
        message: `OS1 Agent branch (${os1AgentBranches.join(", ")}) is not under invoiced branch (${invoicedBranch})`,
      });
    }

    if (!os1AgentProfileObj.active) {
      errorLines.push({
        message: `OS1 Agent profile (${os1AgentProfileNumber}) is not active`,
      });
    }
  } else {
    errorLines.push({
      message: `OS1 Agent profile (${os1AgentProfileNumber}) not found in profiles data`,
    });
  }

  const os2AgentProfileNumber = props.ifInvoice.osAgentNumber2;
  if (os2AgentProfileNumber) {
    const os2AgentProfileObj = profilesData?.find((profile) => profile.profileNumber === os2AgentProfileNumber);
    if (os2AgentProfileObj) {
      const os2AgentBranches = os2AgentProfileObj?.branches || [];

      if (!os2AgentBranches.includes(invoicedBranch)) {
        errorLines.push({
          message: `OS2 Agent branch (${os2AgentBranches.join(", ")}) is not under invoiced branch (${invoicedBranch})`,
        });
      }
      if (!os2AgentProfileObj.active) {
        errorLines.push({
          message: `OS2 Agent profile (${os2AgentProfileNumber}) is not active`,
        });
      }
    } else {
      errorLines.push({
        message: `OS2 Agent profile (${os2AgentProfileNumber}) not found in profiles data`,
      });
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      description: alertCheck.description,
      affectedLines: errorLines,
      message: alertCheck.errorMessage,
    };
  }

  return {
    isValid: true,
    description: alertCheck.description,
    message: alertCheck.successMessage,
  };
}

function validateAlertId6CommissionSplit(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { references, reservationLines, ifInvoice } = props;
  const agentDetails = references?.agentDetails || [];
  const errorLines: ValidationResponse["affectedLines"] = [];
  const TOLERANCE_CENTS = 100; // $1 tolerance

  for (const reservation of reservationLines || []) {
    const reservationDateYear = new Date(reservation.reservation.reservationDate || "").getFullYear();

    for (const ifInvoiceDetail of reservation.ifInvoiceDetails || []) {
      const totalCommissionCents = toCents(ifInvoiceDetail.commissionAmount ?? 0);
      const { osAgentCommission1, osAgentCommission2 } = ifInvoiceDetail;
      const os1CommissionCents = toCents(osAgentCommission1 ?? 0);
      const os2CommissionCents = toCents(osAgentCommission2 ?? 0);
      const agencyCommissionCents = os1CommissionCents + os2CommissionCents;
      const publishedFareCents = toCents(ifInvoiceDetail.publishedFare ?? 0);
      const sellingFareCents = toCents(ifInvoiceDetail.sellingFare ?? 0);
      const extraCommissionCents = sellingFareCents - publishedFareCents;
      const netCommissionCents = totalCommissionCents + extraCommissionCents;

      if (netCommissionCents === 0 && agencyCommissionCents !== 0) {
        errorLines.push({
          message: "Total commission is zero but agency commission is not zero",
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }

      if (totalCommissionCents === 0) {
        continue;
      }

      if (isNaN(reservationDateYear)) {
        errorLines.push({
          message: "Reservation date is invalid",
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }
      const currentYear = new Date().getFullYear();
      const agentId = parseInt(ifInvoice.osAgentNumber2 || ifInvoice.osAgentNumber1 || "");

      let risingStarLevel: number | null | undefined;

      if (reservationDateYear === currentYear) {
        risingStarLevel = props.risingStarLevel;
      } else if (reservationDateYear === 2024 || reservationDateYear === 2025) {
        const agentEntry = risingStarData[agentId];
        risingStarLevel = agentEntry
          ? agentEntry[`y${reservationDateYear}` as "y2024" | "y2025"]
          : undefined;
      } else {
        errorLines.push({
          message: `Rising star level data is not available for year ${reservationDateYear}`,
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }

      if (risingStarLevel === undefined || risingStarLevel === null) {
        errorLines.push({
          message: `Rising star level is not available`,
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }

      if (risingStarLevel < 1 || risingStarLevel > 13) {
        errorLines.push({
          message: `Rising star level ${risingStarLevel} is out of bounds (should be between 1 and 13)`,
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }

      let agencyPercentage = 0;
      //service fees check
      if (
        ifInvoiceDetail.productCode === 18 &&
        ifInvoiceDetail.vendorNumber &&
        parseInt(ifInvoiceDetail.vendorNumber) === 1800
      ) {
        agencyPercentage = 85;
      } else {
        //non-service fees check
        const productTierObj = references?.productTiers?.find(
          (productTier) => productTier.vendorNumber === ifInvoiceDetail.vendorNumber,
        );

        if (!productTierObj) {
          errorLines.push({
            message: "Product tier details not found",
            reservationId: reservation.reservation.reservationId,
            vendorName: reservation.reservation.vendorName,
            bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          });
          continue;
        }

        const resolvedProductTier = resolveProductTier(productTierObj, ifInvoiceDetail.productCode);

        if (!resolvedProductTier || !["preferred", "approved", "others"].includes(resolvedProductTier.toLowerCase())) {
          errorLines.push({
            message: "Product tier is invalid",
            reservationId: reservation.reservation.reservationId,
            vendorName: reservation.reservation.vendorName,
            bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          });
          continue;
        }
        agencyPercentage =
          risingStarMap[risingStarLevel as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13][
            resolvedProductTier.toLowerCase() as "preferred" | "approved" | "others"
          ];
      }

      const expectedAgencyCommissionCents = Math.round(netCommissionCents * (agencyPercentage / 100));
      const agencyCommissionDifference = Math.abs(agencyCommissionCents - expectedAgencyCommissionCents);
      if (agencyCommissionDifference > TOLERANCE_CENTS) {
        errorLines.push({
          message: `Agency commission percentage is not the agreed percentage of ${agencyPercentage}%`,
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }
      //commission check between agents.
      if (ifInvoice.osAgentNumber1 && ifInvoice.osAgentNumber2) {
        const os1Detail = agentDetails.find((agent) => agent.profileNumber === ifInvoice.osAgentNumber1);

        const expectedOs1CommissionCents = Math.round(
          agencyCommissionCents * ((os1Detail?.commissionSplit || 0) / 100),
        );

        const os1CommissionDifference = Math.abs(os1CommissionCents - expectedOs1CommissionCents);
        if (os1CommissionDifference > TOLERANCE_CENTS) {
          errorLines.push({
            message: `Commission split between agents (${(os1CommissionCents / 100).toFixed(2)}/${(
              os2CommissionCents / 100
            ).toFixed(2)}) is not as per their agreement (${os1Detail?.commissionSplit}%)`,
            reservationId: reservation.reservation.reservationId,
            vendorName: reservation.reservation.vendorName,
            bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          });
        }
      }
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId18LocallyPaidSaleType(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { reservationLines, references } = props;
  const errorLines: ValidationResponse["affectedLines"] = [];
  const locallyPaidProductCodes = [2, 3, 13, 32];

  let locallyPaidPresent = false;

  for (const reservation of reservationLines || []) {
    const saleTypeObj = props.references?.saleTypes?.find(
      (saleType) =>
        saleType.profileNumber === reservation.reservation.vendorNumber &&
        saleType.productCode === reservation.reservation.productCode,
    );
    const saleTypeCode = saleTypeObj ? saleTypeObj.saleType : undefined;

    if (saleTypeCode === 2 && locallyPaidProductCodes.includes(reservation.reservation.productCode)) {
      locallyPaidPresent = true;
      continue;
    }

    if (saleTypeCode !== 2 && !locallyPaidProductCodes.includes(reservation.reservation.productCode)) {
      continue;
    }

    if (saleTypeCode === 2 && !locallyPaidProductCodes.includes(reservation.reservation.productCode)) {
      locallyPaidPresent = true;
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: `Sale type for product code ${reservation.reservation.productCode} is 'Voucher' but it is not marked as locally paid`,
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
      continue;
    }

    if (locallyPaidProductCodes.includes(reservation.reservation.productCode) && saleTypeCode !== 2) {
      locallyPaidPresent = true;
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: `Product code ${reservation.reservation.productCode} is marked as locally paid but its sale type is not 'Voucher'`,
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
      continue;
    }
  }

  if (!locallyPaidPresent) {
    return {
      isValid: true,
      message: "No locally paid product codes or sale type 'Voucher' present",
      description: alertCheck.description,
    };
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId13ReservationCommissionAmount(
  alertCheck: AlertCheck,
  props: ValidateInvoiceProps,
): ValidationResponse {
  const { reservationLines } = props;
  const errorLines: ValidationResponse["affectedLines"] = [];
  const GIFT_CERTIFICATE_PRODUCT_CODE = 9;
  const MERCHANT_FEES_VENDOR_NUMBER = 76;
  for (const reservation of reservationLines || []) {
    if (
      reservation.reservation.productCode === GIFT_CERTIFICATE_PRODUCT_CODE ||
      parseInt(reservation.reservation.vendorNumber || "") === MERCHANT_FEES_VENDOR_NUMBER
    ) {
      continue;
    }
    if (!reservation.reservation.commissionAmount) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Reservation commission amount is not present",
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
    }
  }
  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId12FormOfPayment(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { reservationLines } = props;
  const errorLines: ValidationResponse["affectedLines"] = [];
  const validFOPs = [0, 2];
  let serviceFeesPresent = false;

  for (const reservation of reservationLines || []) {
    if (reservation.reservation.productCode !== 18) {
      continue;
    }
    serviceFeesPresent = true;
    for (const ifInvoiceDetail of reservation.ifInvoiceDetails || []) {
      if (
        ifInvoiceDetail.grossAmount === 0 ||
        ifInvoiceDetail.grossAmount === null ||
        ifInvoiceDetail.grossAmount === undefined
      ) {
        continue;
      }
      const formOfPayment = ifInvoiceDetail.payments?.[0]?.formOfPayment;
      const authorizationCode = ifInvoiceDetail.payments?.[0]?.authorizationNumber;

      if (formOfPayment === undefined || formOfPayment === null) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: "Form of payment not found for service fees",
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }

      if (!validFOPs.includes(formOfPayment)) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: "Invalid form of payment for service fees",
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }

      if (formOfPayment === 2 && !authorizationCode) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: "Authorization not found for In-house form of payment for service fees",
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
        continue;
      }
    }
  }

  if (!serviceFeesPresent) {
    return {
      isValid: true,
      message: "No service fees present - Form of payment check skipped",
      description: alertCheck.description,
    };
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId8AmountDueZero(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const errorLines: ValidationResponse["affectedLines"] = [];

  let allSaletypeVouchers = true;
  for (const reservation of props.reservationLines || []) {
    const saleTypeObj = props.references?.saleTypes?.find(
      (saleType) =>
        saleType.profileNumber === reservation.reservation.vendorNumber &&
        saleType.productCode === reservation.reservation.productCode,
    );
    const saleTypeCode = saleTypeObj ? saleTypeObj.saleType : undefined;
    if (saleTypeCode === 2) {
      continue;
    }
    allSaletypeVouchers = false;
    const arInvoicesGrossAmountCents = reservation.arInvoiceDetails.reduce((total, arInvoiceDetail) => {
      return total + toCents(arInvoiceDetail.grossAmount ?? 0);
    }, 0);

    const ifInvoicesGrossAmountCents = reservation.ifInvoiceDetails.reduce((total, ifInvoiceDetail) => {
      return total + toCents(ifInvoiceDetail.grossAmount ?? 0);
    }, 0);

    const arPaymentAmountCents = reservation.arInvoiceDetails.reduce((total, arInvoiceDetail) => {
      return total + toCents(arInvoiceDetail.payments.reduce((acc, payment) => acc + (payment.amount ?? 0), 0));
    }, 0);

    const ifPaymentAmountCents = reservation.ifInvoiceDetails.reduce((total, ifInvoiceDetail) => {
      return total + toCents(ifInvoiceDetail.payments.reduce((acc, payment) => acc + (payment.amount ?? 0), 0));
    }, 0);

    const amountDue =
      arInvoicesGrossAmountCents + ifInvoicesGrossAmountCents - (arPaymentAmountCents + ifPaymentAmountCents);

    if (amountDue !== 0) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: `Amount due is not zero: ${(amountDue / 100).toFixed(2)}`,
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  if (allSaletypeVouchers) {
    return {
      isValid: true,
      message: "All sale types are vouchers",
      description: alertCheck.description,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId17InvoiceNumberMatch(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const errorLines: ValidationResponse["affectedLines"] = [];
  const { arInvoices, ifInvoice } = props;

  if (arInvoices.length === 0) {
    return {
      isValid: true,
      message: "No AR Invoices found",
      description: alertCheck.description,
    };
  }

  if (arInvoices.length > 0) {
    for (const arInvoice of arInvoices) {
      if (arInvoice.invoiceNumber !== ifInvoice.invoiceNumber) {
        errorLines.push({
          message: `AR Invoice number (${arInvoice.invoiceNumber}) does not match IF Invoice number (${ifInvoice.invoiceNumber})`,
        });
      }
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId7BookingLineFormOfPayment(
  alertCheck: AlertCheck,
  props: ValidateInvoiceProps,
): ValidationResponse {
  const { reservationLines } = props;
  const errorLines: ValidationResponse["affectedLines"] = [];
  let allSaletypeVouchers = true;
  for (const reservation of reservationLines || []) {
    let hasPaymentMissing = false;

    const saleTypeObj = props.references?.saleTypes?.find(
      (saleType) =>
        saleType.profileNumber === reservation.reservation.vendorNumber &&
        saleType.productCode === reservation.reservation.productCode,
    );
    const saleTypeCode = saleTypeObj ? saleTypeObj.saleType : undefined;
    if (saleTypeCode === 2) {
      continue;
    }
    allSaletypeVouchers = false;
    for (const ifInvoiceDetail of reservation.ifInvoiceDetails || []) {
      if (
        ifInvoiceDetail.grossAmount === 0 ||
        ifInvoiceDetail.grossAmount === null ||
        ifInvoiceDetail.grossAmount === undefined
      ) {
        continue;
      }
      const payments = ifInvoiceDetail.payments;
      if (
        !payments ||
        payments.length === 0 ||
        payments.some((p) => p.formOfPayment === undefined || p.formOfPayment === null)
      ) {
        hasPaymentMissing = true;
        break;
      }
    }
    if (hasPaymentMissing) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Form of payment not found",
        vendorName: reservation.reservation.vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
    }
  }

  if (allSaletypeVouchers) {
    return {
      isValid: true,
      message: "All sale types are vouchers",
      description: alertCheck.description,
    };
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId16VendorNameNumeric(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const errorLines: ValidationResponse["affectedLines"] = [];

  for (const reservation of props.reservationLines || []) {
    const { vendorName, vendorNumber } = reservation.reservation;
    if (!vendorName) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Vendor name not found",
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        vendorName: vendorName,
      });
    }

    if (!vendorNumber) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Vendor number not found",
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        vendorName: vendorName,
      });
    }

    if (!vendorName || !vendorNumber) {
      continue;
    }

    const isVendorNumberNan = isNaN(Number(vendorNumber));

    if (isVendorNumberNan) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Vendor number is not numeric",
        vendorName: vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
    }

    const isNan = isNaN(Number(vendorName));
    if (!isNan) {
      errorLines.push({
        reservationId: reservation.reservation.reservationId,
        message: "Vendor name is numeric",
        vendorName: vendorName,
        bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
      });
    }
  }
  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId9CurrencyCode(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { branchNumber } = props.ifInvoice;
  const errorLines: ValidationResponse["affectedLines"] = [];

  if (!branchNumber) {
    return {
      isValid: false,
      message: "Branch number not found",
      description: alertCheck.description,
      affectedLines: [{ message: "Branch number not found" }],
    };
  }

  const { reservationLines } = props;

  for (const reservation of reservationLines || []) {
    for (const ifInvoiceDetail of reservation.ifInvoiceDetails || []) {
      const currencyCode = ifInvoiceDetail.currencyCode;
      if (!currencyCode) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: "Currency code not found",
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          vendorName: reservation.reservation.vendorName,
        });
        continue;
      }
      const usdBranch = (branchNumber >= 900 && branchNumber <= 996) || (branchNumber >= 1900 && branchNumber <= 1999);
      if (usdBranch && currencyCode !== "USD") {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: "Currency code is not USD for USD branch",
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
          vendorName: reservation.reservation.vendorName,
        });
      }

      // Validate FOP currency code and invalid FOPs
      for (const payment of ifInvoiceDetail.payments || []) {
        const matchedFop = getMatchingFop(payment);

        if (Array.isArray(matchedFop)) {
          errorLines.push({
            reservationId: reservation.reservation.reservationId,
            message: `Multiple matching FOPs found for form of payment code ${payment.formOfPayment}`,
            bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
            vendorName: reservation.reservation.vendorName,
          });
          continue;
        }

        if (matchedFop) {
          if (matchedFop.invalid) {
            errorLines.push({
              reservationId: reservation.reservation.reservationId,
              message: `Invalid form of payment: ${matchedFop.description}`,
              bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
              vendorName: reservation.reservation.vendorName,
            });
          }

          // Check if FOP currency matches invoice currency (only for FOPs with currencyCode)
          if (matchedFop.currencyCode && matchedFop.currencyCode !== currencyCode) {
            errorLines.push({
              reservationId: reservation.reservation.reservationId,
              message: `FOP currency mismatch: Invoice is ${currencyCode} but FOP (${matchedFop.description}) is ${matchedFop.currencyCode}`,
              bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
              vendorName: reservation.reservation.vendorName,
            });
          }
        }
      }
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId1BranchNo1(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { branchNumber } = props.ifInvoice;
  const errorLines: ValidationResponse["affectedLines"] = [];
  if (!branchNumber) {
    errorLines.push({ message: "Branch number not found" });
  } else if (branchNumber === 1) {
    errorLines.push({ message: "Branch number is 1" });
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId3StartsWithBranchNo(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { invoiceNumber, branchNumber } = props.ifInvoice;

  if (!invoiceNumber) {
    return {
      isValid: false,
      message: "Invoice number not found",
      description: alertCheck.description,
      affectedLines: [{ message: "Invoice number not found" }],
    };
  }

  if (!branchNumber) {
    return {
      isValid: false,
      message: "Branch number not found",
      description: alertCheck.description,
    };
  }

  if (invoiceNumber.startsWith(branchNumber.toString())) {
    return {
      isValid: true,
      message: alertCheck.successMessage,
      description: alertCheck.description,
    };
  }

  return {
    isValid: false,
    message: alertCheck.errorMessage,
    description: alertCheck.description,
    affectedLines: [{ message: "Invoice number does not start with branch number" }],
  };
}

function validateAlertId4TwoAgentsPresent(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { osAgentNumber1, osAgentNumber2, bookingAgentNumber, ticketingAgentNumber } = props.ifInvoice;
  const agentDetails = props.references?.agentDetails;

  const errorLines: ValidationResponse["affectedLines"] = [];

  if (!osAgentNumber1) {
    errorLines.push({ message: "OS Agent number 1 is not present" });
  }

  if (!bookingAgentNumber) {
    errorLines.push({ message: "Booking agent number is not present" });
  }

  if (!ticketingAgentNumber) {
    errorLines.push({ message: "Ticketing agent number is not present" });
  }

  if (!osAgentNumber1 || !bookingAgentNumber || !ticketingAgentNumber) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  if (osAgentNumber1 !== bookingAgentNumber) {
    errorLines.push({
      message: `OS Agent number 1 (${osAgentNumber1}) is not equal to booking agent number (${bookingAgentNumber})`,
    });
  }

  if (osAgentNumber1 !== ticketingAgentNumber) {
    errorLines.push({
      message: `OS Agent number 1 (${osAgentNumber1}) is not equal to ticketing agent number (${ticketingAgentNumber})  `,
    });
  }

  const osAgent1Detail = agentDetails?.find((agent) => agent.profileNumber === osAgentNumber1);
  if (osAgent1Detail) {
    if (!osAgentNumber2 && osAgent1Detail.primaryAgent === false) {
      errorLines.push({
        message: "OS Agent 2 is not present and OS Agent 1 is not primary",
      });
    }
  } else {
    errorLines.push({ message: "OS Agent 1 details not found in reference data" });
  }

  const osAgent2Detail = agentDetails?.find((agent) => agent.profileNumber === osAgentNumber2);
  if (osAgentNumber2 && osAgent2Detail) {
    if (osAgent2Detail.primaryAgent === false) {
      errorLines.push({
        message: "OS Agent 2 is present but OS Agent 2 is not primary",
      });
    }

    if (osAgent1Detail?.primaryAgent === true) {
      errorLines.push({
        message: "OS Agent 2 is present but OS Agent 1 is primary",
      });
    }
  } else {
    if (osAgentNumber2) {
      errorLines.push({ message: "OS Agent 2 details not found in reference data" });
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}

function validateAlertId2PostingMonth(alertCheck: AlertCheck, props: ValidateInvoiceProps): ValidationResponse {
  const { invoiceDate, dueDate } = props.ifInvoice;
  const config = props.interfaceValidatorConfig;
  const postingMonth = config?.value.postingMonth;

  const errorLines: ValidationResponse["affectedLines"] = [];

  for (const reservation of props.reservationLines || []) {
    for (const ifInvoiceDetail of reservation.ifInvoiceDetails || []) {
      const { transactionDate } = ifInvoiceDetail;

      if (!postingMonth) {
        errorLines.push({
          message: "Posting month is not present",
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }

      if (!invoiceDate) {
        errorLines.push({
          message: "Invoice date is not present",
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }

      if (!transactionDate) {
        errorLines.push({
          message: "Transaction date is not present",
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }

      if (!dueDate) {
        errorLines.push({
          message: "Due date is not present",
          reservationId: reservation.reservation.reservationId,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }

      if (!dueDate || !invoiceDate || !transactionDate || !postingMonth) {
        continue;
      }

      // Parse posting month string (format: YYYYMM)
      const postingYear = parseInt(postingMonth.substring(0, 4));
      const postingMonthNum = parseInt(postingMonth.substring(4, 6));

      const isoInvoiceDate = new Date(invoiceDate);
      const isoDueDate = new Date(dueDate);
      const isoTransactionDate = new Date(transactionDate);

      const transactionYear = isoTransactionDate.getFullYear();
      const transactionMonth = isoTransactionDate.getMonth() + 1;
      const invoiceYear = isoInvoiceDate.getFullYear();
      const invoiceMonth = isoInvoiceDate.getMonth() + 1;
      const dueYear = isoDueDate.getFullYear();
      const dueMonth = isoDueDate.getMonth() + 1;

      if (transactionYear !== postingYear || transactionMonth !== postingMonthNum) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: `Transaction date (${transactionYear}-${String(transactionMonth).padStart(
            2,
            "0",
          )}) does not match posting month (${postingMonth})`,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }

      if (invoiceYear !== postingYear || invoiceMonth !== postingMonthNum) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: `Invoice date (${invoiceYear}-${String(invoiceMonth).padStart(
            2,
            "0",
          )}) does not match posting month (${postingMonth})`,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }

      if (dueYear !== postingYear || dueMonth !== postingMonthNum) {
        errorLines.push({
          reservationId: reservation.reservation.reservationId,
          message: `Due date (${dueYear}-${String(dueMonth).padStart(
            2,
            "0",
          )}) does not match posting month (${postingMonth})`,
          vendorName: reservation.reservation.vendorName,
          bookingNumber: reservation.reservation.ticketNumber || reservation.reservation.recordLocator,
        });
      }
    }
  }

  if (errorLines.length > 0) {
    return {
      isValid: false,
      message: alertCheck.errorMessage,
      description: alertCheck.description,
      affectedLines: errorLines,
    };
  }

  return {
    isValid: true,
    message: alertCheck.successMessage,
    description: alertCheck.description,
  };
}
